import { useEffect, useRef } from "react";

/* ============================================================
   TANGIG — Section cinématique "Le Mouvement"
   Clone du mécanisme "2026" (Le Labo) : le mot entre immense,
   se pose puis rétrécit ; les 4 phrases s'enchaînent au même
   rythme. Fond BLANC pur (#FFFFFF), scroll épinglé.
   La section grimpe sur le hero (épinglé) au premier scroll,
   puis l'animation du mot se joue une fois plein écran.
   À placer juste après le hero. Usage : <LeMouvement />.
   ============================================================ */

/* Fenêtres de fondu des phrases — identiques à la section 2026
   pour retrouver exactement la même vitesse de transition. */
const PHRASE_WINDOWS = [
  [0.48, 0.54, 0.60, 0.64],
  [0.62, 0.68, 0.74, 0.78],
  [0.76, 0.82, 0.87, 0.90],
  [0.90, 0.96, null, null],
];

const CSS = `
  /* z-index: 2 → la section passe AU-DESSUS du hero (épinglé, z-index: 1)
     et grimpe donc par-dessus lui pendant le premier écran de scroll. */
  .lm-track { position: relative; z-index: 2; height: 410vh; background: #FFFFFF; width: 100%; }
  .lm-stage {
    position: sticky; top: 0; height: 100vh; overflow: hidden; background: #FFFFFF;
    display: flex; align-items: center; justify-content: center;
    font-family: var(--font-sans, Inter, sans-serif);
  }
  /* Halos bleus retires : la section est en blanc pur, sans reflet colore. */
  .lm-halo { display: none; }
  .lm-halo-warm { display: none; }

  /* Le mot "Le mouvement" — noir, gras, scale + opacity pilotés au scroll.
     Il n'apparait (opacity 0 -> 1) qu'une fois la section plein ecran ;
     pendant la montee sur le hero il reste invisible. */
  .lm-wordpos { position: absolute; top: 50%; left: 50%; width: 100%;
    display: flex; justify-content: center; transform: translate(-50%,-50%);
    will-change: transform; pointer-events: none; }
  .lm-word {
    font-size: clamp(2rem, 6vw, 5.4rem); font-weight: 800; letter-spacing: -0.02em;
    line-height: 1; white-space: nowrap; transform-origin: center center;
    will-change: transform, filter, opacity; color: #000000; opacity: 0;
  }

  .lm-phrases { position: absolute; top: 60%; left: 50%; transform: translate(-50%,-50%);
    width: min(1000px, 90vw); text-align: center; pointer-events: none; }
  .lm-phrase { position: absolute; inset: 0; margin: auto; height: fit-content;
    font-size: clamp(30px, 4.4vw, 56px); font-weight: 600; letter-spacing: -0.02em;
    line-height: 1.18; color: #1D1D1F; opacity: 0; will-change: opacity, transform; }
  .lm-phrase--last { position: relative; }
  .lm-accent { color: #000000; font-weight: 800; }
  .lm-soft { color: #86868B; font-weight: 400; }
  .lm-final { font-size: clamp(40px, 6vw, 84px); font-weight: 700; letter-spacing: -0.025em; line-height: 1.05; color: #1D1D1F; }
  .lm-sub { margin-top: 24px; font-size: clamp(16px, 1.6vw, 20px); font-weight: 400; color: #6E6E73; letter-spacing: -0.01em; }

  .lm-hint { position: absolute; bottom: 34px; left: 50%; transform: translateX(-50%);
    display: flex; flex-direction: column; align-items: center; gap: 8px; color: #6E6E73; }
`;

export default function LeMouvement() {
  const trackRef = useRef(null);
  const wordposRef = useRef(null);
  const wordRef = useRef(null);
  const warmRef = useRef(null);
  const hintRef = useRef(null);
  const phrasesRef = useRef([]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const clamp = (v) => Math.min(1, Math.max(0, v));
    const smooth = (a, b, t) => { const x = clamp((t - a) / (b - a)); return x * x * (3 - 2 * x); };
    const seg = (p, a, b) => clamp((p - a) / (b - a));

    let raf = null;
    const render = () => {
      raf = null;
      const rect = track.getBoundingClientRect();
      const total = track.offsetHeight - window.innerHeight;
      const p = total > 0 ? clamp(-rect.top / total) : 0;

      // Révélation : le mot reste invisible tant que la section grimpe sur le
      // hero (rect.top > 0) ; il apparait juste après le verrouillage plein
      // écran (rect.top passe sous 0), sur ~0.15 écran de scroll.
      const reveal = smooth(0, -0.15 * window.innerHeight, rect.top);

      // Entrée : le mot arrive immense (2.0×), se pose (1.0×) puis rétrécit
      // légèrement (0.7×) en remontant pour laisser place aux phrases.
      const enter = smooth(0, 0.30, p);       // 2.0× → 1.0×
      const settle = smooth(0.34, 0.44, p);   // 1.0× → 0.7× + remonte
      const scale = (2.0 - enter * 1.0) - settle * 0.30;
      const blur = (1 - smooth(0, 0.08, p)) * 5;
      const ty = -settle * 22; // vh

      if (wordposRef.current) wordposRef.current.style.transform = `translate(-50%, -50%) translateY(${ty.toFixed(2)}vh)`;
      if (wordRef.current) {
        wordRef.current.style.transform = `scale(${scale.toFixed(4)})`;
        wordRef.current.style.filter = blur > 0.05 ? `blur(${blur.toFixed(2)}px)` : "none";
        wordRef.current.style.opacity = reveal.toFixed(3);
      }
      if (warmRef.current) warmRef.current.style.opacity = (seg(p, 0.72, 0.9) * 0.9).toFixed(3);
      if (hintRef.current) hintRef.current.style.opacity = (reveal * (1 - smooth(0, 0.08, p))).toFixed(3);

      phrasesRef.current.forEach((el, i) => {
        if (!el) return;
        const [inA, inB, outA, outB] = PHRASE_WINDOWS[i];
        const fadeIn = seg(p, inA, inB);
        const fadeOut = outA == null ? 1 : 1 - seg(p, outA, outB);
        el.style.opacity = Math.min(fadeIn, fadeOut).toFixed(3);
        el.style.transform = `translateY(${((1 - fadeIn) * 16).toFixed(1)}px)`;
      });
    };

    const onScroll = () => { if (raf == null) raf = requestAnimationFrame(render); };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    render();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section ref={trackRef} className="lm-track" data-navbar-theme="light" data-screen-label="Le Mouvement">
      <style>{CSS}</style>
      <div className="lm-stage">
        <div className="lm-halo" />
        <div ref={warmRef} className="lm-halo-warm" />

        {/* Le mot immense, dégradé bleu lumineux */}
        <div ref={wordposRef} className="lm-wordpos">
          <div ref={wordRef} className="lm-word">Le mouvement</div>
        </div>

        {/* Récit : phrases empilées, fondu enchaîné */}
        <div className="lm-phrases">
          <div ref={(el) => (phrasesRef.current[0] = el)} className="lm-phrase">
            Vendre un fichier est terminé.
          </div>
          <div ref={(el) => (phrasesRef.current[1] = el)} className="lm-phrase">
            Place aux systèmes qui produisent un résultat.
          </div>
          <div ref={(el) => (phrasesRef.current[2] = el)} className="lm-phrase">
            <span className="lm-accent">Né pour les créateurs francophones.</span>
            <br />
            <span className="lm-soft">Pensé pour rayonner au-delà des frontières.</span>
          </div>
          <div ref={(el) => (phrasesRef.current[3] = el)} className="lm-phrase lm-phrase--last">
            <div className="lm-final">
              La nouvelle génération
              <br />
              d'actifs digitaux.
            </div>
            <div className="lm-sub">
              Vous ne vendez plus un fichier. Vous livrez une transformation.
            </div>
          </div>
        </div>

        {/* indice de scroll */}
        <div ref={hintRef} className="lm-hint">
          <span style={{ fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase" }}>Faites défiler</span>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 5v14M6 13l6 6 6-6" />
          </svg>
        </div>
      </div>
    </section>
  );
}
